import { useMemo, memo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { MultiLineDnd } from 'common-components'
import { usePlayheadOverlappingItems } from 'hooks/timeline'
import { convertAudioItemsToTimelineItems } from 'utils/timeline'
import { TDndListItems } from 'common-components/DndList/types'

import { editorThunks } from 'store/features/editor/thunks'
import { editorSelectors } from 'store/features/editor/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'

import { IAudioItem } from 'types/audio'
import { secondsToPixels, pixelsToSeconds } from 'utils/timeline'
import TrackItem from './TrackItem'
import { dispatch } from 'store'
import { TIMELINE_CONTENT_WRAPPER_ID } from '../../consts'
import { AUDIO_TRACK_HEIGHT } from './consts'
import * as Styled from './styled'
import { ttsSelectors } from 'store/features/tts/selectors'
import { useItemClickHandler } from '../hooks/use-item-click-handler'
import { COLORS } from 'consts/colors'

const AudioTracks = (props: { preventOnClick: boolean }) => {
  const { preventOnClick } = props
  const isDraggingDisabled = useSelector(ttsSelectors.isGenerationInProcess)
  const audioItemsFromStore = useSelector(projectDataSelectors.multilineAudios)
  const [audioItems, setAudioItems] =
    useState<IAudioItem[]>(audioItemsFromStore)

  useEffect(() => {
    setAudioItems(audioItemsFromStore)
  }, [audioItemsFromStore])

  const isAnyBuffering = useSelector(editorSelectors.isAnyBuffering)
  const timelineItems = useMemo(
    () => convertAudioItemsToTimelineItems(audioItems),
    [audioItems]
  )
  const overlappingItemIds = usePlayheadOverlappingItems({
    isAnyItemBuffering: isAnyBuffering,
    timelineItems
  })

  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )

  const activeTimelineItems = useSelector(editorSelectors.activeTimelineItems)

  const handler = useItemClickHandler()

  const dndData = useMemo(
    () =>
      audioItems.map(item => {
        const { id, projectStart, duration, lineIndex, type } = item
        const isActive = activeTimelineItems.some(
          activeItem => activeItem.id === id && activeItem.type === type
        )
        const isOverlappingPlayHead = overlappingItemIds.includes(id)
        const isResizeBtnsVisible = activeTimelineItems.length <= 1

        // disable dnd kit  dragging in multiple items dragging mode
        const isItemInMemberOfActiveItems = isActive && !isResizeBtnsVisible

        return {
          id,
          left: secondsToPixels(projectStart, timelineRulerSliceDuration),
          width: secondsToPixels(duration, timelineRulerSliceDuration),
          lineIndex,
          disabled: isDraggingDisabled,
          isDraggingDisabled: isItemInMemberOfActiveItems,
          content: (
            <TrackItem
              data={item}
              isActive={isActive}
              isOverlappingPlayHead={isOverlappingPlayHead}
              onClick={event => {
                if (!preventOnClick) {
                  handler({ id, type, event, isActive })
                }
              }}
              updateAudioItems={setAudioItems}
              isResizeBtnsVisible={isResizeBtnsVisible}
              selectedBorderColor={COLORS.secondarySemiDark}
              selectedBorderSize={2}
            />
          )
        }
      }),
    // TODO: Recheck this
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      audioItems,
      timelineRulerSliceDuration,
      activeTimelineItems,
      isDraggingDisabled,
      overlappingItemIds,
      preventOnClick
    ]
  )

  const handleItemsUpdate = (updatedItems: TDndListItems) => {
    const updatedMediaItems = updatedItems.map(updatedItem => {
      const audioItem = audioItems.find(
        item => item.id === updatedItem.id
      ) as IAudioItem

      return {
        ...audioItem,
        lineIndex: updatedItem.lineIndex,
        projectStart: pixelsToSeconds(
          updatedItem.left,
          timelineRulerSliceDuration
        ),
        duration: pixelsToSeconds(updatedItem.width, timelineRulerSliceDuration)
      }
    })

    dispatch(projectDataThunks.setMultilineAudios(updatedMediaItems))
  }

  const handleDragStart = () => {
    dispatch(editorThunks.setIsTimelineItemDragging(true))
  }

  const handleDragEnd = (updatedItems: TDndListItems) => {
    handleItemsUpdate(updatedItems)
    dispatch(editorThunks.setIsTimelineItemDragging(false))
  }

  return (
    <Styled.AudioTracks>
      <MultiLineDnd
        data={dndData}
        dropLineSettings={{
          style: {
            height: AUDIO_TRACK_HEIGHT
          },
          className: 'audio-track'
        }}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        boundingContainerId={TIMELINE_CONTENT_WRAPPER_ID}
      />
    </Styled.AudioTracks>
  )
}

export default memo(AudioTracks)
